import React from 'react';
import { Box } from '@mui/material';

// Live energy-flow diagram (like the vendor mobile apps): Solar · Grid (MLGW) · Battery · Home
// wired with right-angle pipes to a central inverter, energy animating along each pipe in the
// direction the live readings imply.
//   power_w : solar PV (>=0, into the inverter when producing)
//   grid_w  : import positive (grid -> inverter) · export negative (inverter -> grid)
//   batt_w  : discharge positive (battery -> inverter) · charge negative (inverter -> battery)
//   load_w  : home consumption (>=0, inverter -> home)
const C = {
  solar: '#BA7517', grid: '#185FA5', battery: '#3B6D11', home: '#993C1D',
  pipe: '#cbd5e1', surface: '#ffffff', inv: '#64748b', border: '#e2e8f0',
  textP: '#334155', textS: '#64748b', textM: '#94a3b8',
};
const ACTIVE_W = 20; // ignore sub-20W noise so a trickle doesn't animate
const kwText = (w) => `${(Math.abs(Number(w || 0)) / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} kW`;

// dir: 'in' = node -> inverter, 'out' = inverter -> node, null = inactive
function dirOf(kind, v) {
  const n = Number(v);
  if (!Number.isFinite(n) || Math.abs(n) < ACTIVE_W) return null;
  if (kind === 'solar') return 'in';
  if (kind === 'load') return 'out';
  if (kind === 'grid' || kind === 'battery') return n > 0 ? 'in' : 'out';
  return null;
}

function Pipe({ id, d, color, dir }) {
  return (
    <>
      <path d={d} fill="none" stroke={C.pipe} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      <path id={id} d={d} fill="none" stroke={color} strokeWidth={5} strokeLinejoin="round"
        className={`ef-flow${dir ? ` ef-${dir}` : ''}`} style={{ opacity: dir ? 1 : 0 }} />
    </>
  );
}

function Card({ x, y, color, active, label, value, sub, icon }) {
  return (
    <g>
      <rect x={x} y={y} width={104} height={80} rx={11} fill={C.surface}
        stroke={active ? color : C.border} strokeWidth={active ? 1.8 : 1} />
      {icon}
      <text x={x + 52} y={y + 44} textAnchor="middle" style={{ fontSize: 12, fill: C.textS }}>{label}</text>
      <text x={x + 52} y={y + 61} textAnchor="middle" style={{ fontSize: 16, fontWeight: 500, fill: C.textP }}>{value}</text>
      <text x={x + 52} y={y + 74} textAnchor="middle" style={{ fontSize: 10, fill: C.textS }}>{sub}</text>
    </g>
  );
}

export default function EnergyFlowDiagram({ solarW, gridW, battW, loadW, soc, inverterLabel = 'Inverter' }) {
  const dSolar = dirOf('solar', solarW);
  const dGrid = dirOf('grid', gridW);
  const dBatt = dirOf('battery', battW);
  const dHome = dirOf('load', loadW);

  const gridSub = Number(gridW) > ACTIVE_W ? 'Importing' : Number(gridW) < -ACTIVE_W ? 'Exporting' : 'Idle';
  const battState = Number(battW) > ACTIVE_W ? 'Discharging' : Number(battW) < -ACTIVE_W ? 'Charging' : 'Idle';
  const battSub = soc != null ? `${battState} · ${Math.round(soc)}%` : battState;

  return (
    <Box sx={{ width: '100%', maxWidth: 440, mx: 'auto', my: 1 }}>
      <style>{`
        .ef-flow { stroke-dasharray: 2 9; stroke-linecap: round; }
        .ef-in  { animation: ef-in 1s linear infinite; }
        .ef-out { animation: ef-out 1s linear infinite; }
        @keyframes ef-in  { to { stroke-dashoffset: -11; } }
        @keyframes ef-out { to { stroke-dashoffset: 11; } }
        @media (prefers-reduced-motion: reduce) { .ef-flow { animation: none; } }
      `}</style>
      <svg width="100%" viewBox="0 0 340 300" role="img" aria-label="Live energy flow between solar, grid, battery and home">
        {/* inverter */}
        <rect x={135} y={114} width={70} height={72} rx={8} fill={C.surface} stroke={C.inv} strokeWidth={1.5} />
        <text x={170} y={147} textAnchor="middle" style={{ fontSize: 11, fill: C.textS }}>{inverterLabel}</text>
        <text x={170} y={161} textAnchor="middle" style={{ fontSize: 10, fill: C.textM }}>inverter</text>

        {/* pipes (base + animated flow) */}
        <Pipe id="ef-solar" d="M64,94 V132 H135" color={C.solar} dir={dSolar} />
        <Pipe id="ef-grid" d="M276,94 V132 H205" color={C.grid} dir={dGrid} />
        <Pipe id="ef-batt" d="M64,206 V168 H135" color={C.battery} dir={dBatt} />
        <Pipe id="ef-home" d="M276,206 V168 H205" color={C.home} dir={dHome} />

        {/* inverter ports */}
        {[[135, 132], [135, 168], [205, 132], [205, 168]].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={4} fill={C.surface} stroke={C.inv} strokeWidth={1.5} />
        ))}

        <Card x={12} y={14} color={C.solar} active={!!dSolar} label="Solar" value={kwText(solarW)} sub={dSolar ? 'Producing' : 'Idle'}
          icon={(
            <g>
              <circle cx={64} cy={34} r={5} fill="none" stroke={C.solar} strokeWidth={1.6} />
              <g stroke={C.solar} strokeWidth={1.6} strokeLinecap="round">
                <line x1={64} y1={25} x2={64} y2={22} /><line x1={64} y1={43} x2={64} y2={46} />
                <line x1={55} y1={34} x2={52} y2={34} /><line x1={73} y1={34} x2={76} y2={34} />
                <line x1={57} y1={27} x2={55} y2={25} /><line x1={71} y1={41} x2={73} y2={43} />
                <line x1={71} y1={27} x2={73} y2={25} /><line x1={57} y1={41} x2={55} y2={43} />
              </g>
            </g>
          )} />

        <Card x={224} y={14} color={C.grid} active={!!dGrid} label="Grid · MLGW" value={kwText(gridW)} sub={gridSub}
          icon={<path d="M279,24 L272,36 L277,36 L274,45 L285,33 L280,33 L283,24 Z" fill={C.grid} />} />

        <Card x={12} y={206} color={C.battery} active={!!dBatt} label="Battery" value={kwText(battW)} sub={battSub}
          icon={(
            <g>
              <rect x={52} y={221} width={22} height={11} rx={2} fill="none" stroke={C.battery} strokeWidth={1.6} />
              <rect x={74} y={224} width={2.5} height={5} rx={1} fill={C.battery} />
              <rect x={54} y={223} width={Math.max(2, Math.min(18, 18 * (Number(soc || 0) / 100)))} height={7} rx={1} fill={C.battery} />
            </g>
          )} />

        <Card x={224} y={206} color={C.home} active={!!dHome} label="Home" value={kwText(loadW)} sub={dHome ? 'Consuming' : 'Idle'}
          icon={(
            <g>
              <path d="M268,230 L276,222 L284,230" fill="none" stroke={C.home} strokeWidth={1.6} strokeLinejoin="round" />
              <rect x={270} y={230} width={12} height={9} fill="none" stroke={C.home} strokeWidth={1.6} />
            </g>
          )} />
      </svg>
    </Box>
  );
}
